// @ts-nocheck
/**
 * Where the Live Sheet's rows come from.
 *
 * An organiser reads the tables directly under their own JWT (RLS decides).
 * A share-link recipient has no access to any of it, so their datasets come
 * back from the sheet-share edge function, which applies the token's rules
 * server-side and strips hidden columns out of the payload entirely.
 */
import { supabase } from "@/integrations/supabase/client";

export interface SheetDatasets {
  registrations: any[];
  profiles: Record<string, any>;
  constraints: any[];
  withdrawals: any[];
  eventCfg: { roster_deadline: string | null; roster_minimum_players: number | null };
  tracked: any[];
  ageGroups: string[];
  levels: string[];
  /** Present only on a share link. */
  share?: ShareInfo;
  /** Plain, recipient-facing message when a link is revoked/expired/not theirs. */
  denied?: string;
  error?: string;
}

export interface ShareInfo {
  label: string | null;
  permission: "view" | "edit";
  expires_at: string | null;
  hide_money_columns: boolean;
  hide_checkin_columns: boolean;
  organization_name: string;
  event_id: string;
  event_name: string;
}

const empty = (): SheetDatasets => ({
  registrations: [],
  profiles: {},
  constraints: [],
  withdrawals: [],
  eventCfg: { roster_deadline: null, roster_minimum_players: null },
  tracked: [],
  ageGroups: [],
  levels: [],
});

async function fetchAsOrganiser(eventId: string): Promise<SheetDatasets> {
  const regs = await supabase
    .from("event_registrations_v2")
    .select("*")  // teams live on RecruitLook — names are fetched from there
    .eq("event_id", eventId)
    .order("registered_at", { ascending: true });
  if (regs.error) return { ...empty(), error: regs.error.message };

  const base = regs.data ?? [];
  const coachIds = [
    ...new Set(
      base.map((r: any) => r.coach_user_id || r.registered_by_user_id).filter(Boolean)
    ),
  ];

  const [profileRows, constraints, withdrawals, eventCfg, tracked, ages, levels] =
    await Promise.all([
      coachIds.length
        ? supabase
            .from("profiles")
            .select("id, first_name, last_name, email, phone")
            .in("id", coachIds as string[])
        : Promise.resolve({ data: [] as any[] }),
      supabase
        .from("schedule_constraints")
        .select("team_id, constraint_type, parameters")
        .eq("event_id", eventId)
        .is("deleted_at", null),
      supabase
        .from("event_registration_withdrawals")
        .select(
          "registration_id, reason, reason_details, created_at, status, refund_amount_cents, refund_processed_at"
        )
        .eq("event_id", eventId)
        .order("created_at", { ascending: true }),
      supabase
        .from("events")
        .select("roster_deadline, roster_minimum_players")
        .eq("id", eventId)
        .maybeSingle(),
      supabase
        .from("event_team_tracking")
        .select("*")
        .eq("event_id", eventId)
        .is("converted_registration_id", null)
        .order("created_at", { ascending: true }),
      supabase.from("event_age_groups").select("name").eq("event_id", eventId),
      supabase
        .from("event_registration_levels")
        .select("label")
        .eq("event_id", eventId)
        .eq("is_active", true)
        .order("sort_order"),
    ]);

  const profiles: Record<string, any> = {};
  for (const p of (profileRows as any).data ?? []) profiles[p.id] = p;

  return {
    registrations: base,
    profiles,
    constraints: constraints.data ?? [],
    withdrawals: withdrawals.data ?? [],
    eventCfg: {
      roster_deadline: (eventCfg.data as any)?.roster_deadline ?? null,
      roster_minimum_players: (eventCfg.data as any)?.roster_minimum_players ?? null,
    },
    tracked: tracked.data ?? [],
    ageGroups: (ages.data ?? []).map((a: any) => a.name),
    levels: (levels.data ?? []).map((l: any) => l.label),
  };
}

async function fetchAsRecipient(token: string): Promise<SheetDatasets> {
  const { data, error } = await supabase.functions.invoke("sheet-share", {
    body: { token },
  });
  if (error) {
    return {
      ...empty(),
      denied: "This sheet couldn't be opened. Please contact the event organiser.",
    };
  }
  if (data?.denied) return { ...empty(), denied: data.message };

  const d = data?.data ?? {};
  const profiles: Record<string, any> = {};
  for (const p of d.profiles ?? []) profiles[p.id] = p;

  return {
    registrations: d.registrations ?? [],
    profiles,
    constraints: d.constraints ?? [],
    withdrawals: d.withdrawals ?? [],
    eventCfg: {
      roster_deadline: d.event?.roster_deadline ?? null,
      roster_minimum_players: d.event?.roster_minimum_players ?? null,
    },
    tracked: d.tracked ?? [],
    ageGroups: d.age_groups ?? [],
    levels: d.levels ?? [],
    share: data?.share,
  };
}

export function fetchSheetDatasets(
  eventId: string,
  shareToken?: string
): Promise<SheetDatasets> {
  return shareToken ? fetchAsRecipient(shareToken) : fetchAsOrganiser(eventId);
}

/** Grid columns withheld by each "hide from them" checkbox. */
export const MONEY_COLUMN_IDS = [
  "__payments",
  "__comp",
  "payment_status",
  "total_amount_due",
  "total_amount_paid",
  "amount_owed",
  "payment_method_chosen",
  "payment_received_at",
  "special_requests_fee",
  "extras_total",
  "discount_total",
  "primary_billing_email",
];

export const CHECKIN_COLUMN_IDS = [
  "__withdraw",
  "registration_status",
  "roster_state",
  "roster_status",
  "admin_notes",
];
